import { useQuery } from "react-query";
import { Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline } from "@mdi/js";

import { CriteriaEntityGetResponse } from "src/entities/CriteriaEntity";
import { CriteriaGroupEntity } from "src/entities/CriteriaGroupEntity";
import { CriteriaClassificationEntity } from "src/entities/CriteriaClassificationEntity";
import { CriteriaManifestationEntity } from "src/entities/CriteriaManifestationEntity";
import { CriteriaRiskEntity } from "src/entities/CriteriaRiskEntity";
import { CriteriaRecomendationEntity } from "src/entities/CriteriaRecomendationEntity";
import { CriteriaDomainGetResponse } from "src/entities/CriteriaDomainEntity";
import CriteriaService, { criteriaQueryKeys } from "src/services/CriteriaService";
import CriteriaGroupService, { criteriaGroupQueryKeys } from "src/services/CriteriaGroupService";
import CriteriaClassificationService, { criteriaClassificationQueryKeys } from "src/services/CriteriaClassificationService";
import CriteriaManifestationService, { criteriaManifestationQueryKeys } from "src/services/CriteriaManifestationService";
import CriteriaRiskService, { criteriaRiskQueryKeys } from "src/services/CriteriaRiskService";
import CriteriaRecomendationService, { criteriaRecomendationQueryKeys } from "src/services/CriteriaRecomendationService";
import CriteriaDomainService, { criteriaDomainQueryKeys } from "src/services/CriteriaDomainService";

const criteriaService = new CriteriaService();
const criteriaGroupService = new CriteriaGroupService();
const criteriaClassificationService = new CriteriaClassificationService();
const criteriaManifestationService = new CriteriaManifestationService();
const criteriaRiskService = new CriteriaRiskService();
const criteriaRecomendationService = new CriteriaRecomendationService();
const criteriaDomainService = new CriteriaDomainService();

type CriteriaPreviewProps = {
	criteriaId: number;
};

export default function CriteriaPreview({ criteriaId }: CriteriaPreviewProps) {
	const id = String(criteriaId);

	const { data, isLoading: criteriaLoading } = useQuery<CriteriaEntityGetResponse>(
		criteriaQueryKeys.get(id),
		() => criteriaService.get(criteriaId).then(response => response.data),
		{ refetchOnWindowFocus: false }
	);

	const { data: groups, isLoading: groupsLoading } = useQuery<CriteriaGroupEntity[]>(
		criteriaGroupQueryKeys.groups(id),
		() => criteriaGroupService.getGroups(criteriaId).then(response => response.data.groups),
		{ refetchOnWindowFocus: false }
	);

	const { data: classifications, isLoading: classificationsLoading } = useQuery<CriteriaClassificationEntity[]>(
		criteriaClassificationQueryKeys.classifications(id),
		() => criteriaClassificationService.getClassifications(criteriaId).then(response => response.data.classifications),
		{ refetchOnWindowFocus: false }
	);

	const { data: manifestations, isLoading: manifestationsLoading } = useQuery<CriteriaManifestationEntity[]>(
		criteriaManifestationQueryKeys.manifestations(id),
		() => criteriaManifestationService.getManifestations(criteriaId).then(response => response.data.manifestations),
		{ refetchOnWindowFocus: false }
	);

	const { data: risks, isLoading: risksLoading } = useQuery<CriteriaRiskEntity[]>(
		criteriaRiskQueryKeys.risks(id),
		() => criteriaRiskService.getRisks(criteriaId).then(response => response.data.risks),
		{ refetchOnWindowFocus: false }
	);

	const { data: recomendations, isLoading: recomendationsLoading } = useQuery<CriteriaRecomendationEntity[]>(
		criteriaRecomendationQueryKeys.recomendations(id),
		() => criteriaRecomendationService.getRecomendations(criteriaId).then(response => response.data.recomendations),
		{ refetchOnWindowFocus: false }
	);

	const { data: domainsData, isLoading: domainsLoading } = useQuery<CriteriaDomainGetResponse>(
		criteriaDomainQueryKeys.domains(id),
		() => criteriaDomainService.getDomains(criteriaId).then(response => response.data),
		{ refetchOnWindowFocus: false }
	);

	return (
		<>
			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Critério
				</Card.Header>
				<Card.Body>
					{criteriaLoading ? (
						<>
							<Form.Group className="mb-3">
								<div className="skeleton skeleton-text skeleton-text-title mb-2" style={{ height: 16, width: "10%" }} />
								<div className="skeleton skeleton-button" style={{ height: 38 }} />
							</Form.Group>
							<Form.Group className="mb-3">
								<div className="skeleton skeleton-text skeleton-text-title mb-2" style={{ height: 16, width: "10%" }} />
								<div className="skeleton skeleton-button" style={{ height: 120 }} />
							</Form.Group>
						</>
					) : (
						<>
						<Form.Group className="mb-3" controlId="titulo">
							<Form.Label className="text-muted"><small>Título</small></Form.Label>
							<Form.Control type="text" disabled defaultValue={data?.title} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="texto">
							<Form.Label className="text-muted"><small>Texto</small></Form.Label>
							<Form.Control as="textarea" rows={5} disabled value={data?.text ?? ""} />
						</Form.Group>
						</>
					)}
				</Card.Body>
			</Card>

			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Grupos
				</Card.Header>
				<Card.Body>
					{groupsLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<Row key={skeletonIndex} className="mb-3">
								<Col>
									<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
									<div className="skeleton skeleton-button" style={{ height: 38 }} />
								</Col>
							</Row>
						))
					) : groups?.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						groups?.map((group, index) => (
							<Row key={index} className="mb-3">
								<Col>
									<Form.Label className="text-muted"><small>Título</small></Form.Label>
									<Form.Control type="text" disabled value={group.title} />
								</Col>
							</Row>
						))
					)}
				</Card.Body>
			</Card>

			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Classificações
				</Card.Header>
				<Card.Body>
					{classificationsLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<Row key={skeletonIndex} className="mb-3">
								<Col>
									<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
									<div className="skeleton skeleton-button" style={{ height: 38 }} />
								</Col>
							</Row>
						))
					) : classifications?.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						classifications?.map((item, index) => (
							<Row key={index} className="mb-3">
								<Col>
									<Form.Label className="text-muted"><small>Título</small></Form.Label>
									<Form.Control type="text" disabled value={item.title} />
								</Col>
							</Row>
						))
					)}
				</Card.Body>
			</Card>

			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Manifestações
				</Card.Header>
				<Card.Body>
					{manifestationsLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<Row key={skeletonIndex} className="mb-3 align-items-end">
								<Col md={12} lg={4}>
									<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
									<div className="skeleton skeleton-button" style={{ height: 38 }} />
								</Col>
								<Col md={6} lg={4}>
									<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
									<div className="skeleton skeleton-button" style={{ height: 38 }} />
								</Col>
								<Col md={6} lg={4}>
									<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
									<div className="skeleton skeleton-button" style={{ height: 38 }} />
								</Col>
							</Row>
						))
					) : manifestations?.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						manifestations?.map((item, index) => (
							<div
								key={index}
								className={index < manifestations.length - 1 ? "border-bottom pb-4 mb-3" : "mb-3"}
							>
								<Row className="mb-2 gy-2">
									<Col md={12} lg={4}>
										<Form.Label className="text-muted"><small>Título</small></Form.Label>
										<Form.Control type="text" disabled value={item.title} />
									</Col>
									<Col md={6} lg={4}>
										<Form.Label className="text-muted"><small>Importância Técnica</small></Form.Label>
										<Form.Control type="text" disabled value={item.technicalImportance ?? ""} />
									</Col>
									<Col md={6} lg={4}>
										<Form.Label className="text-muted"><small>Importância Funcional</small></Form.Label>
										<Form.Control type="text" disabled value={item.functionalImportance ?? ""} />
									</Col>
								</Row>
								<Row>
									<Col md={12} lg={6}>
										<Form.Label className="text-muted"><small>Classificações</small></Form.Label>
										<Form.Control type="text" disabled value={item.classifications.map(classification => classification.title).join(", ")} />
									</Col>
									<Col md={12} lg={6}>
										<Form.Label className="text-muted"><small>Grupos</small></Form.Label>
										<Form.Control type="text" disabled value={item.groups.map(group => group.title).join(", ")} />
									</Col>
								</Row>
							</div>
						))
					)}
				</Card.Body>
			</Card>

			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Riscos
				</Card.Header>
				<Card.Body>
					{risksLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<div key={skeletonIndex} className="mb-3">
								<Row className="mb-2 gy-2">
									<Col md={6}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col md={6}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
								</Row>
								<Row>
									<Col md={12}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
								</Row>
							</div>
						))
					) : risks?.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						risks?.map((risk, index) => (
							<div
								key={index}
								className={index < risks.length - 1 ? "border-bottom pb-4 mb-3" : "mb-3"}
							>
								<Row className="mb-2 gy-2">
									<Col md={6}>
										<Form.Label className="text-muted"><small>Título</small></Form.Label>
										<Form.Control type="text" disabled value={risk.title} />
									</Col>
									<Col md={6}>
										<Form.Label className="text-muted"><small>Descrição</small></Form.Label>
										<Form.Control type="text" disabled value={risk.description} />
									</Col>
								</Row>
								<Row>
									<Col md={12}>
										<Form.Label className="text-muted"><small>Manifestações</small></Form.Label>
										<Form.Control type="text" disabled value={risk.manifestations?.map(manifestation => manifestation.title).join(", ") ?? ""} />
									</Col>
								</Row>
							</div>
						))
					)}
				</Card.Body>
			</Card>

			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Recomendações
				</Card.Header>
				<Card.Body>
					{recomendationsLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<div key={skeletonIndex} className="mb-3">
								<Row className="mb-2 gy-2">
									<Col md={6}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col md={6}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
								</Row>
								<Row>
									<Col md={12}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
								</Row>
							</div>
						))
					) : recomendations?.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						recomendations?.map((recomendation, index) => (
							<div
								key={index}
								className={index < recomendations.length - 1 ? "border-bottom pb-4 mb-3" : "mb-3"}
							>
								<Row className="mb-2 gy-2">
									<Col md={6}>
										<Form.Label className="text-muted"><small>Título</small></Form.Label>
										<Form.Control type="text" disabled value={recomendation.title} />
									</Col>
									<Col md={6}>
										<Form.Label className="text-muted"><small>Descrição</small></Form.Label>
										<Form.Control type="text" disabled value={recomendation.description} />
									</Col>
								</Row>
								<Row>
									<Col md={12}>
										<Form.Label className="text-muted"><small>Manifestações</small></Form.Label>
										<Form.Control type="text" disabled value={recomendation.manifestations?.map(manifestation => manifestation.title).join(", ") ?? ""} />
									</Col>
								</Row>
							</div>
						))
					)}
				</Card.Body>
			</Card>

			{domainsLoading ? (
				Array.from({ length: 2 }, (_, skeletonIndex) => (
					<Card key={skeletonIndex} className="mb-4">
						<Card.Header className="d-flex align-items-center bg-white fs-5 fw-light p-3">
							<div className="skeleton skeleton-text skeleton-text-title" style={{ height: 16, width: "20%" }} />
						</Card.Header>
						<Card.Body>
							<div className="skeleton skeleton-button mb-3" style={{ height: 24, width: "15%" }} />
							{Array.from({ length: 3 }, (_, rowIndex) => (
								<div key={rowIndex} className="skeleton skeleton-button mb-2" style={{ height: 38 }} />
							))}
						</Card.Body>
					</Card>
				))
			) : !domainsData?.domains?.length ? (
				<Card className="mb-4">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
						Domínios
					</Card.Header>
					<Card.Body>
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					</Card.Body>
				</Card>
			) : (
				domainsData.domains.map((domain, domainIndex) => (
					<Card key={domainIndex} className="mb-4">
						<Card.Header className="d-flex align-items-center bg-white fs-5 fw-light p-3">
							<div className="d-flex flex-column lh-1">
								<div className="fs-6">
									<small className="text-muted fw-light">Domínio</small>
								</div>
								{domain.title}
							</div>
						</Card.Header>
						<Card.Body>
							<Form.Group className="mb-3 col-md-3">
								<Form.Label className="text-muted"><small>Obrigatório</small></Form.Label>
								<Form.Check
									type="switch"
									className="pt-1"
									disabled
									checked={domain.required}
									readOnly
								/>
							</Form.Group>
							{domain.options.map((option, optionIndex) => (
								<Row key={optionIndex} className="mb-2">
									<Col md={12} lg={5}>
										<InputGroup>
											<InputGroup.Text>
												<small className="text-muted">Título</small>
											</InputGroup.Text>
											<Form.Control type="text" disabled value={option.title} />
										</InputGroup>
									</Col>
									<Col md={6} lg={3}>
										<InputGroup>
											<InputGroup.Text>
												<small className="text-muted">Valor</small>
											</InputGroup.Text>
											<Form.Control type="text" disabled value={option.value} />
										</InputGroup>
									</Col>
									<Col md={6} lg={4}>
										<InputGroup>
											<Form.Check
												type="switch"
												className="d-flex gap-2 pt-1"
												label={option.enabled ? "Habilitado" : "Desabilitado"}
												disabled
												checked={option.enabled}
												readOnly
											/>
										</InputGroup>
									</Col>
								</Row>
							))}
						</Card.Body>
					</Card>
				))
			)}
		</>
	);
}
